import BackButton from '../../components/BackButton';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import feather from 'feather-icons';
import BottomNavbar from '../../components/Home/BottomNavbar';
import MonthlyStatistics from '../../components/Home/MonthlyStatistics';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
    amountDisplay,
    getUserTimezone,
    normalizeMonthParam,
    dateDisplay,
    numberLanguageWise,
} from '../../util/helper';
import serverClient from '../../api/ServerClient';
import { DeleteToken } from '../../auth/Auth';
import Alert from '../../components/Alert';
import Loader from '../../components/Loader';
import { useLanguage } from '../../context/LanguageContext';
import { getPageMeta } from '../../util/seoHelper';
import { FiArrowUpRight, FiArrowDownLeft, FiTrendingUp } from 'react-icons/fi';
import '../../styles/CashFlowReportPage.css';

function CashFlowReport() {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    const { language, t } = useLanguage();
    const navigate = useNavigate();
    const timeZone = getUserTimezone();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cashFlowData, setCashFlowData] = useState(null);

    const [customMonth, setCustomMonth] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const month = searchParams.get('month');
    const type = searchParams.get('type');
    const [dataMonth, setDataMonth] = useState(month);

    // Initialize reporting type from query param
    const initialReportingType = type === 'yearly' ? 'yearly' : 'monthly';
    const [reportingType, setReportingType] = useState(initialReportingType);
    const isYearly = reportingType === 'yearly';

    // Sync search params — only called explicitly from user interactions
    // (never from a mount-triggered effect), so the initial page load never
    // pushes a duplicate history entry that would require pressing Back twice.
    const syncSearchParams = (nextMonth, nextReportingType) => {
        const newParams = { type: nextReportingType };
        if (nextMonth) {
            newParams.month = normalizeMonthParam(nextMonth);
        }
        setSearchParams(newParams);
    };

    const currentPeriodStart = dataMonth
        ? dayjs(dataMonth).tz(timeZone)
        : dayjs()
              .tz(timeZone)
              .startOf(isYearly ? 'year' : 'month');

    const monthTitle = useMemo(() => {
        if (isYearly) {
            return numberLanguageWise(currentPeriodStart.format('YYYY'), language);
        }
        if (month !== null && !isNaN(new Date(month).getTime())) {
            return dateDisplay(currentPeriodStart, language, 'monthOnly');
        }
        return t('today');
    }, [month, isYearly, language, currentPeriodStart, t]);

    const lastMonthTitle = useMemo(() => {
        if (isYearly) {
            return numberLanguageWise((currentPeriodStart.year() - 1).toString(), language);
        }
        if (month !== null && !isNaN(new Date(month).getTime())) {
            return dateDisplay(currentPeriodStart.subtract(1, 'month'), language, 'monthOnly');
        }
        return t('lastMonth');
    }, [month, isYearly, language, currentPeriodStart, t]);

    const handlePrevMonth = (e) => {
        e.preventDefault();
        let prevDate;
        if (isYearly) {
            prevDate = currentPeriodStart.subtract(1, 'year').startOf('year').format('YYYY-MM-DD');
        } else {
            prevDate = currentPeriodStart
                .subtract(1, 'month')
                .startOf('month')
                .format('YYYY-MM-DD');
        }
        setDataMonth(prevDate);
        syncSearchParams(prevDate, reportingType);
    };

    const handleThisMonth = () => {
        setDataMonth(null);
        syncSearchParams(null, reportingType);
    };

    const handleReportingTypeChange = (newType) => {
        // Keep current date but when switching to yearly, normalize to year start
        let nextDate = dataMonth;
        if (newType === 'yearly' && dataMonth) {
            nextDate = dayjs(dataMonth).tz(timeZone).startOf('year').format('YYYY-MM-DD');
            setDataMonth(nextDate);
        }
        setReportingType(newType);
        syncSearchParams(nextDate, newType);
    };

    const handleApplyCustomMonth = () => {
        const date = dayjs(customMonth).tz(timeZone).format('YYYY-MM-01');
        setDataMonth(date);
        syncSearchParams(date, reportingType);

        const modalEl = document.getElementById('period');
        const modalInstance = window.bootstrap.Modal.getInstance(modalEl);
        modalInstance?.hide();
    };

    const fetchCashFlowData = useCallback(async () => {
        try {
            setLoading(true);
            let uri;
            if (dataMonth) {
                uri = `/api/user/report/cash-flow?month=${normalizeMonthParam(dataMonth)}&type=${reportingType}`;
            } else {
                uri = `/api/user/report/cash-flow?type=${reportingType}`;
            }
            const response = await serverClient.get(`${uri}`);
            setCashFlowData(response?.data);
        } catch (e) {
            if (e.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
                navigate('/login');
            } else {
                setError(t('somethingWentWrong'));
            }
        } finally {
            setLoading(false);
        }
    }, [dataMonth, reportingType, t, navigate]);

    useEffect(() => {
        fetchCashFlowData();
    }, [fetchCashFlowData]);

    useEffect(() => {
        feather.replace();
    });

    return (
        <>
            <Helmet>
                <title>{getPageMeta('cashFlowReport', language).title}</title>
                <meta
                    name="description"
                    content={getPageMeta('cashFlowReport', language).description}
                />
                <meta name="keywords" content={getPageMeta('cashFlowReport', language).keywords} />
                <link rel="canonical" href={getPageMeta('cashFlowReport', language).url} />
                {getPageMeta('cashFlowReport', language).noindex && (
                    <meta name="robots" content="noindex, follow" />
                )}

                {/* Open Graph Tags */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={getPageMeta('cashFlowReport', language).title} />
                <meta
                    property="og:description"
                    content={getPageMeta('cashFlowReport', language).description}
                />
                <meta property="og:url" content={getPageMeta('cashFlowReport', language).url} />
                <meta property="og:image" content={getPageMeta('cashFlowReport', language).image} />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content={getPageMeta('cashFlowReport', language).title}
                />
                <meta
                    name="twitter:description"
                    content={getPageMeta('cashFlowReport', language).description}
                />
                <meta
                    name="twitter:image"
                    content={getPageMeta('cashFlowReport', language).image}
                />
            </Helmet>
            <div className="modal add-money-modal fade" id="period" tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title">{t('selectPeriod')}</h2>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="inputfromdate" className="form-label">
                                    {isYearly ? t('year') : t('fromDate')}
                                </label>
                                <input
                                    type={isYearly ? 'number' : 'month'}
                                    className="form-control"
                                    id="inputfromdate"
                                    value={isYearly ? customMonth.split('-')[0] : customMonth}
                                    onChange={(e) => {
                                        if (isYearly) {
                                            setCustomMonth(e.target.value);
                                        } else {
                                            setCustomMonth(e.target.value);
                                        }
                                    }}
                                    max={dayjs()
                                        .tz(timeZone)
                                        .format(isYearly ? 'YYYY' : 'YYYY-MM')}
                                    min={isYearly ? '2020' : undefined}
                                />
                            </div>

                            <button
                                className="btn theme-btn successfully w-100 mt-4"
                                onClick={handleApplyCustomMonth}
                            >
                                {t('apply')}
                            </button>
                        </div>

                        <button type="button" className="btn close-btn" data-bs-dismiss="modal">
                            <i className="icon" data-feather="x"></i>
                        </button>
                    </div>
                </div>
            </div>

            <Loader loading={loading} />

            <header className="section-t-space">
                <div className="custom-container">
                    <div className="header-panel">
                        <BackButton fallback="/reports" />
                        <h2>{t('cashFlow')}</h2>

                        <div className="dropdown">
                            <Link
                                to="#"
                                className="back-btn"
                                role="button"
                                data-bs-toggle="dropdown"
                            >
                                <i className="icon" data-feather="settings"></i>
                            </Link>

                            <ul className="dropdown-menu">
                                <li>
                                    <button
                                        type="button"
                                        className={`dropdown-item${reportingType === 'monthly' ? ' fw-semibold text-primary' : ''}`}
                                        onClick={() => handleReportingTypeChange('monthly')}
                                    >
                                        {t('monthly')}
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className={`dropdown-item${reportingType === 'yearly' ? ' fw-semibold text-primary' : ''}`}
                                        onClick={() => handleReportingTypeChange('yearly')}
                                    >
                                        {t('yearly')}
                                    </button>
                                </li>
                                <li>
                                    <hr className="dropdown-divider" />
                                </li>
                                <li>
                                    <Link className="dropdown-item" to="" onClick={handleThisMonth}>
                                        {isYearly ? t('thisYear') : t('thisMonth')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="#"
                                        onClick={handlePrevMonth}
                                        className="dropdown-item"
                                    >
                                        {isYearly ? t('lastYear') : t('lastMonth')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        className="dropdown-item"
                                        to="#period"
                                        data-bs-toggle="modal"
                                    >
                                        {t('custom')}
                                    </Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </header>

            <div className="cashflow-page">
                {error && (
                    <section className="section-b-space">
                        <div className="custom-container">
                            <Alert alert={'danger'}>{error}</Alert>
                        </div>
                    </section>
                )}

                {/* ---------- Current period ---------- */}
                <section>
                    <div className="custom-container">
                        <div className="cf-hero cf-hero--current">
                            <div className="cf-hero-top">
                                <span className="cf-hero-label">
                                    {t('earnings')} | {monthTitle}
                                </span>
                                <h2 className="cf-hero-amount">
                                    {amountDisplay(
                                        cashFlowData?.currentMonthData?.net_balance ?? 0,
                                        language
                                    )}
                                </h2>
                            </div>
                            <div className="cf-tiles">
                                <div className="cf-tile cf-tile--income">
                                    <div className="cf-tile-top">
                                        <div className="cf-tile-icon">
                                            <FiArrowUpRight />
                                        </div>
                                        <div className="cf-tile-text">
                                            <h5>{t('income')}</h5>
                                            <h3>
                                                {amountDisplay(
                                                    cashFlowData?.currentMonthData?.total_income ??
                                                        0,
                                                    language
                                                )}
                                            </h3>
                                        </div>
                                    </div>
                                    <div className="cf-progress">
                                        <div
                                            className="cf-progress-bar"
                                            style={{
                                                width: `${cashFlowData?.currentMonthData?.income_progress ?? 0}%`,
                                            }}
                                        ></div>
                                    </div>
                                </div>
                                <div className="cf-tile cf-tile--expense">
                                    <div className="cf-tile-top">
                                        <div className="cf-tile-icon">
                                            <FiArrowDownLeft />
                                        </div>
                                        <div className="cf-tile-text">
                                            <h5>{t('expense')}</h5>
                                            <h3>
                                                {amountDisplay(
                                                    cashFlowData?.currentMonthData
                                                        ?.total_expenses ?? 0,
                                                    language
                                                )}
                                            </h3>
                                        </div>
                                    </div>
                                    <div className="cf-progress">
                                        <div
                                            className="cf-progress-bar"
                                            style={{
                                                width: `${cashFlowData?.currentMonthData?.expense_progress ?? 0}%`,
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ---------- Comparison period ---------- */}
                <section className="section-b-space">
                    <div className="custom-container">
                        <div className="cf-section-label">
                            <FiTrendingUp /> {isYearly ? t('lastYear') : t('lastMonth')}
                        </div>
                        <div className="cf-hero cf-hero--previous">
                            <div className="cf-hero-top">
                                <span className="cf-hero-label">
                                    {t('earnings')} | {lastMonthTitle}
                                </span>
                                <h2 className="cf-hero-amount">
                                    {amountDisplay(
                                        cashFlowData?.previousMonthData?.net_balance ?? 0,
                                        language
                                    )}
                                </h2>
                            </div>
                            <div className="cf-tiles">
                                <div className="cf-tile cf-tile--income">
                                    <div className="cf-tile-top">
                                        <div className="cf-tile-icon">
                                            <FiArrowUpRight />
                                        </div>
                                        <div className="cf-tile-text">
                                            <h5>{t('income')}</h5>
                                            <h3>
                                                {amountDisplay(
                                                    cashFlowData?.previousMonthData?.total_income ??
                                                        0,
                                                    language
                                                )}
                                            </h3>
                                        </div>
                                    </div>
                                    <div className="cf-progress">
                                        <div
                                            className="cf-progress-bar"
                                            style={{
                                                width: `${cashFlowData?.previousMonthData?.income_progress ?? 0}%`,
                                            }}
                                        ></div>
                                    </div>
                                </div>
                                <div className="cf-tile cf-tile--expense">
                                    <div className="cf-tile-top">
                                        <div className="cf-tile-icon">
                                            <FiArrowDownLeft />
                                        </div>
                                        <div className="cf-tile-text">
                                            <h5>{t('expense')}</h5>
                                            <h3>
                                                {amountDisplay(
                                                    cashFlowData?.previousMonthData
                                                        ?.total_expenses ?? 0,
                                                    language
                                                )}
                                            </h3>
                                        </div>
                                    </div>
                                    <div className="cf-progress">
                                        <div
                                            className="cf-progress-bar"
                                            style={{
                                                width: `${cashFlowData?.previousMonthData?.expense_progress ?? 0}%`,
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <MonthlyStatistics
                    performanceData={cashFlowData?.performance}
                    reportingType={reportingType}
                />
            </div>

            <BottomNavbar />
        </>
    );
}

export default CashFlowReport;
